import SEO from '@/components/SEO';
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuoteDrawer } from '../../contexts/QuoteDrawerContext';
import React, { useState, useRef, useEffect } from "react";
import { useTheme } from 'next-themes';
import { 
  BarChart2, Database, Cpu, HardDrive, GitBranch, 
  ChevronDown, MessageSquare, Briefcase, HeartPulse, ShoppingCart, 
  Building2, ArrowRight, Check, Server, Layers, 
  Code2, Users, Clock, Globe, TabletSmartphone,
  Headphones, Wifi, Battery, User, Zap, Shield,
  Star, Phone, ChartBar
} from "lucide-react";
import { RelatedServices } from "@/components/RelatedServices";
import WebDesignLocations from '@/components/WebDesignLocations';

// Brand colors for showcase
const brandColors = [
  '#6366F1', // Indigo
  '#10B981', // Emerald
  '#3B82F6', // Blue
  '#8B5CF6', // Violet
  '#EC4899', // Pink
  '#F59E0B', // Amber
  '#06B6D4'  // Cyan
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const FAQ = ({ question, answer }: { question: string; answer: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-gray-200 pb-4">
      <button
        className="flex justify-between items-center w-full text-left py-3 focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-medium text-lg">{question}</span>
        <ChevronDown className={`h-5 w-5 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="mt-2 text-muted-foreground">
          {answer}
        </div>
      )}
    </div>
  );
};

function DataAnalytics() {
  const { openDrawer } = useQuoteDrawer();
  const { setTheme } = useTheme();

  // Force light mode
  useEffect(() => {
    setTheme('light');
    document.documentElement.classList.remove('dark');
    document.documentElement.style.colorScheme = 'light';
  }, [setTheme]);

  const features = [
    {
      title: "Business Intelligence",
      description: "Transform raw data into actionable insights with interactive dashboards and reports.",
      icon: <BarChart2 className="h-8 w-8" />,
      color: "#3B82F6"
    },
    {
      title: "Data Warehousing",
      description: "Centralized data storage solutions optimized for analytics and reporting.",
      icon: <Database className="h-8 w-8" />,
      color: "#8B5CF6"
    },
    {
      title: "Predictive Analytics",
      description: "Leverage machine learning to forecast trends and identify opportunities.",
      icon: <Cpu className="h-8 w-8" />,
      color: "#EC4899"
    },
    {
      title: "Data Visualization",
      description: "Beautiful, intuitive dashboards that make complex data easy to understand.",
      icon: <HardDrive className="h-8 w-8" />,
      color: "#10B981"
    },
    {
      title: "ETL Pipelines",
      description: "Automated data extraction, transformation and loading processes.",
      icon: <GitBranch className="h-8 w-8" />,
      color: "#F59E0B"
    },
    {
      title: "Real-time Analytics",
      description: "Process and analyze streaming data for instant decision-making.",
      icon: <Zap className="h-8 w-8" />,
      color: "#6366F1"
    }
  ];

  const technologies = [
    { name: "Power BI", icon: <i className="fas fa-chart-line text-4xl text-[#F2C811]"></i>, color: "#F2C811" },
    { name: "Tableau", icon: <i className="fas fa-chart-pie text-4xl text-[#1F5084]"></i>, color: "#1F5084" },
    { name: "Snowflake", icon: <i className="fas fa-snowflake text-4xl text-[#29B5E8]"></i>, color: "#29B5E8" },
    { name: "Looker", icon: <i className="fas fa-eye text-4xl text-[#4285F4]"></i>, color: "#4285F4" },
    { name: "Python", icon: <i className="fab fa-python text-4xl text-[#3776AB]"></i>, color: "#3776AB" },
    { name: "Spark", icon: <i className="fas fa-bolt text-4xl text-[#E25A1C]"></i>, color: "#E25A1C" },
  ];

  const testimonials = [
    {
      name: "Ahmed Khalifa",
      position: "Head of Analytics, Retail Corp",
      quote: "Kecly transformed our data infrastructure and delivered dashboards that our executives actually use daily.",
      rating: 5
    },
    {
      name: "Sarah Johnson",
      position: "CFO, Tech Startup",
      quote: "The predictive models helped us reduce inventory costs by 22% in just 3 months.",
      rating: 5
    },
    {
      name: "Mohammed Al-Mansoori",
      position: "Operations Director, Logistics",
      quote: "Real-time tracking dashboards improved our delivery efficiency by 18%.",
      rating: 4
    }
  ];

  const faqs = [
    {
      question: "How long does it take to implement a data analytics solution?",
      answer: "Implementation timelines vary based on complexity, but most projects range from 4-12 weeks. We start with a discovery phase to understand your specific needs."
    },
    {
      question: "What data sources can you integrate?",
      answer: "We can connect to virtually any data source including databases, APIs, cloud storage, spreadsheets, and SaaS platforms like Salesforce or Shopify."
    },
    {
      question: "Do you provide training for our team?",
      answer: "Yes, we include comprehensive training and documentation to ensure your team can maintain and expand the solution after deployment."
    }
  ];

  return (
    <>
      <SEO 
        title="Data Analytics Services UAE | Kecly" 
        description="Transform your data into actionable insights with our comprehensive analytics solutions."
      />

      {/* Clean White Hero Section */}
      <section className="relative py-24 bg-white text-gray-900 overflow-hidden border-b border-gray-100">
        <div className="container mx-auto px-6 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center bg-purple-100 text-purple-600 rounded-full px-4 py-1.5 mb-6">
              <span className="text-sm font-semibold tracking-wider">DATA-DRIVEN DECISIONS</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-800 to-pink-700">
                Advanced Analytics
              </span>
            </h1>
            <p className="text-xl text-gray-800 mb-8 max-w-2xl mx-auto">
              Transform raw data into strategic insights with our AI-powered analytics platform
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={openDrawer}
                className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-8 py-4 text-lg font-medium rounded-full transition-all hover:shadow-lg shadow-md hover:from-purple-700 hover:to-pink-600"
              >
                Get Started
              </Button>
              <Button 
                variant="outline"
                className="border-purple-600 text-purple-600 hover:bg-purple-50 px-8 py-4 text-lg font-medium rounded-full transition-all"
              >
                <Phone className="w-5 h-5 mr-2" /> +971-58-248-1443
              </Button>
            </div>
          </motion.div>
          
          {/* Stats */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto"
          >
            {[
              { value: "95%", label: "Client Retention", color: "from-purple-600 to-pink-500" },
              { value: "24/7", label: "Real-time Analytics", color: "from-blue-500 to-cyan-400" },
              { value: "300+", label: "Data Sources", color: "from-emerald-500 to-teal-400" },
              { value: "10x", label: "ROI Improvement", color: "from-amber-500 to-orange-400" }
            ].map((stat, index) => (
              <div key={index} className="bg-white p-4 rounded-xl text-center border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <div className={`text-3xl font-bold mb-1 bg-clip-text text-transparent bg-gradient-to-r ${stat.color}`}>
                  {stat.value}
                </div>
                <div className="text-gray-600 text-sm">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Comprehensive Data Analytics Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From data collection to actionable insights, we provide end-to-end analytics solutions
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all"
              >
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: `${feature.color}20` }}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Analytics Technology Stack</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We use industry-leading tools to deliver powerful, scalable analytics solutions
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {technologies.map((tech, index) => (
              <motion.div
                key={tech.name}
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl text-center hover:shadow-md transition-all border border-gray-100"
              >
                {tech.icon}
                <p className="font-medium text-sm text-gray-700 mt-4">{tech.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Client Success Stories</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Don't just take our word for it - hear from our satisfied clients
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all"
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mr-4">
                    <User className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-500">{testimonial.position}</p>
                  </div>
                </div>
                <p className="text-gray-600 mb-4">"{testimonial.quote}"</p>
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`h-5 w-5 ${i < testimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Answers to common questions about our data analytics services
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <FAQ key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <RelatedServices 
            currentService="Data Analytics" 
            services={[
              {
                title: "Cloud Services",
                description: "Scalable, secure, and high-performance cloud solutions for your data needs.",
                icon: <Server className="h-8 w-8" />
              },
              {
                title: "Software Development",
                description: "Custom software solutions to process and analyze your business data.",
                icon: <Code2 className="h-8 w-8" />
              },
              {
                title: "Digital Marketing",
                description: "Data-driven marketing strategies to grow your business.",
                icon: <ChartBar className="h-8 w-8" />
              }
            ]} 
          />
        </div>
      </section>

      {/* Web Design Locations */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <WebDesignLocations />
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to unlock insights from your data?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Let's discuss how we can transform your data into strategic advantage
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={openDrawer}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-medium rounded-lg transition-all hover:shadow-lg"
              >
                Get Started
              </Button>
              <Button 
                variant="outline"
                className="border-blue-200 bg-white text-blue-700 hover:bg-blue-50 px-8 py-3 text-lg font-medium rounded-lg transition-all"
              >
                Call Us: +971-58-248-1443
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}

export default DataAnalytics;
